import { Extension } from '@tiptap/core';
import type { Plugin } from '@tiptap/pm/state';
import { EditorState, Plugin as PMPlugin } from '@tiptap/pm/state';
import type { Slice, Schema } from '@tiptap/pm/model';
import { Slice as PMSlice, Fragment } from '@tiptap/pm/model';
import type { EditorView } from '@tiptap/pm/view';
import { logger } from '@getaccept/lib-shared/src/logger';
import { getHttpsUrl, isValidUrl } from '@getaccept/lib-shared/src/helpers/url.helper';

const addTextWithLineBreaks = (
  text: string,
  inlineNodes: ReturnType<Schema['text']>[],
  schema: Schema
) => {
  let buffer = '';
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (ch === '\n') {
      if (buffer) {
        inlineNodes.push(schema.text(buffer));
        buffer = '';
      }
      const hardBreak = schema.nodes.hard_break || schema.nodes.hardBreak;
      if (hardBreak) {
        inlineNodes.push(hardBreak.create());
      }
      continue;
    }
    buffer += ch;
  }
  if (buffer) {
    inlineNodes.push(schema.text(buffer));
  }
};

const createParagraphSliceFromText = (text: string, schema: Schema) => {
  const inlineNodes: ReturnType<Schema['text']>[] = [];
  const normalized = text.replace(/\r\n/g, '\n');
  const linkMarkType = schema.marks.link;

  const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let matchResult;

  while ((matchResult = markdownLinkRegex.exec(normalized)) !== null) {
    const beforeLink = normalized.slice(lastIndex, matchResult.index);
    const linkText = matchResult[1];
    const linkHref = matchResult[2];

    if (beforeLink) {
      addTextWithLineBreaks(beforeLink, inlineNodes, schema);
    }

    if (linkMarkType && isValidUrl(linkHref)) {
      const linkMark = linkMarkType.create({ href: getHttpsUrl(linkHref) });
      inlineNodes.push(schema.text(linkText, [linkMark]));
    } else {
      inlineNodes.push(schema.text(matchResult[0]));
    }

    ({ lastIndex } = markdownLinkRegex);
  }

  const remainingText = normalized.slice(lastIndex);
  if (remainingText) {
    addTextWithLineBreaks(remainingText, inlineNodes, schema);
  }

  const paragraph = schema.nodes.paragraph.create(
    null,
    inlineNodes.length ? Fragment.from(inlineNodes) : undefined
  );
  return new PMSlice(Fragment.from(paragraph), 0, 0);
};

const flattenSliceToSingleParagraph = (slice: Slice, schema: Schema) => {
  const state = EditorState.create({ schema });
  const { tr, doc: emptyDoc } = state;
  const transaction = tr.replace(0, emptyDoc.content.size, slice);
  const applied = state.apply(transaction);
  const { doc } = applied;
  const plainText = doc.textBetween(0, doc.content.size, '\n');

  return createParagraphSliceFromText(plainText, schema);
};

const extractTextFromHtml = (html: string): string => {
  if (!html) return '';
  try {
    return new DOMParser().parseFromString(html, 'text/html').body.textContent || '';
  } catch {
    return '';
  }
};

export const PlainTextPasteExtension = Extension.create({
  name: 'plainTextPaste',
  addProseMirrorPlugins(): Plugin[] {
    return [
      new PMPlugin({
        props: {
          transformPasted: slice => flattenSliceToSingleParagraph(slice, this.editor.state.schema),
          handleDOMEvents: {
            paste: (view: EditorView, event: ClipboardEvent): boolean => {
              try {
                const plain = event.clipboardData?.getData('text/plain') || '';
                const html = event.clipboardData?.getData('text/html') || '';
                const uri = event.clipboardData?.getData('text/uri-list') || '';

                if (!plain && !html && !uri) {
                  return false;
                }

                const textToInsert = plain || extractTextFromHtml(html) || uri;
                if (!textToInsert) return false;

                const { selection, schema } = view.state;
                const hasSelection: boolean = !selection.empty && selection.from !== selection.to;
                const linkMarkType = schema.marks.link;

                if (!hasSelection && linkMarkType) {
                  const urlRegex =
                    /(?:https?:\/\/)?(?:www\.)?[a-zA-Z0-9][a-zA-Z0-9-]*(?:\.[a-zA-Z0-9][a-zA-Z0-9-]*)+(?:\/[^\s]*)?/;
                  const urlMatch = textToInsert.match(urlRegex);

                  if (urlMatch && isValidUrl(urlMatch[0])) {
                    event.preventDefault();
                    const url = urlMatch[0];
                    const href = getHttpsUrl(url);
                    const linkMark = linkMarkType.create({ href });

                    if (textToInsert.trim() === url) {
                      const tr = view.state.tr
                        .insertText(textToInsert, selection.from)
                        .addMark(selection.from, selection.from + textToInsert.length, linkMark);
                      view.dispatch(tr);
                    } else {
                      const urlStart = urlMatch.index || 0;

                      const tr = view.state.tr.insertText(textToInsert, selection.from);

                      const linkStart = selection.from + urlStart;
                      const linkEnd = linkStart + url.length;

                      tr.addMark(linkStart, linkEnd, linkMark);

                      view.dispatch(tr);
                    }
                    return true;
                  }
                }

                if (hasSelection && linkMarkType && isValidUrl(textToInsert)) {
                  event.preventDefault();
                  const href = getHttpsUrl(textToInsert);
                  const tr = view.state.tr.addMark(
                    selection.from,
                    selection.to,
                    linkMarkType.create({ href })
                  );
                  view.dispatch(tr);
                  return true;
                }

                event.preventDefault();
                const normalized = textToInsert.replace(/\r\n/g, '\n');
                const slice = createParagraphSliceFromText(normalized, view.state.schema);
                const tr = view.state.tr.replaceSelection(slice);
                view.dispatch(tr);
                return true;
              } catch (e) {
                logger.error('[PlainTextPasteExtension] paste handler error', {
                  error: (e as Error)?.message,
                });
                return true;
              }
            },
          },
        },
      }),
    ];
  },
});

export { createParagraphSliceFromText };
